import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import type { GroupChat } from '../types';

// Admin parity (C2·P5). Fetches the operator's group chats — scope mirrors mobile
// useMessages exactly: group_chat_members (my memberships) → group_chats
// (is_active, id IN …) with members joined. RLS already limits group_chats SELECT
// to chats the operator belongs to, so this is the same surface mobile sees.

export function useGroupChats() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const businessId = business?.id;
  const currentUserId = user?.id; // public users.id (loaded via get_public_user_id)
  const mountedRef = useRef(true);

  const [groupChats, setGroupChats] = useState<GroupChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchGroups = useCallback(async () => {
    if (!businessId || !currentUserId) { setLoading(false); return; }
    try {
      const { data: myGroups, error: memErr } = await supabase
        .from('group_chat_members')
        .select('group_chat_id')
        .eq('user_id', currentUserId);
      if (memErr) throw memErr;
      if (!mountedRef.current) return;

      const groupIds = (myGroups ?? []).map((g: { group_chat_id: string }) => g.group_chat_id);
      if (groupIds.length === 0) {
        setGroupChats([]);
        setLoading(false);
        return;
      }

      const { data: gData, error: gErr } = await supabase
        .from('group_chats')
        .select('*, raw_members:group_chat_members(user:users(id, first_name, last_name))')
        .in('id', groupIds)
        .eq('is_active', true)
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (gErr) throw gErr;
      if (!mountedRef.current) return;

      const groups: GroupChat[] = (gData ?? []).map((g: Record<string, unknown>) => {
        const members = ((g.raw_members ?? []) as { user: GroupChat['members'][number] | null }[])
          .map(m => m.user)
          .filter(Boolean) as GroupChat['members'];
        return {
          id: g.id as string,
          business_id: g.business_id as string,
          chat_type: g.chat_type as string,
          name: g.name as string,
          icon: (g.icon as string | null) ?? null,
          context_type: (g.context_type as string | null) ?? null,
          context_id: (g.context_id as string | null) ?? null,
          is_active: (g.is_active as boolean) ?? true,
          last_message_at: (g.last_message_at as string | null) ?? null,
          last_message_preview: (g.last_message_preview as string | null) ?? null,
          created_at: g.created_at as string,
          members,
          memberCount: members.length,
        };
      });
      setGroupChats(groups);
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed to load group chats');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, currentUserId]);

  useEffect(() => { void fetchGroups(); }, [fetchGroups]);

  // Realtime: refetch when group chats or messages change for this business.
  useEffect(() => {
    if (!businessId) return;
    const ch = supabase
      .channel(`admin-groups-${businessId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_chats', filter: `business_id=eq.${businessId}` }, () => { void fetchGroups(); })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `business_id=eq.${businessId}` }, () => { void fetchGroups(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [businessId, fetchGroups]);

  return { groupChats, loading, error, refresh: fetchGroups };
}
